import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import AppLayout from './components/AppLayout';
import LoginPage from './pages/LoginPage';
import { ConfigProvider } from 'antd';

import PurchasingPage from './pages/PurchasingPage';
import PurchaseForm from './pages/PurchaseForm';
import PurchaseDetailsPage from './pages/PurchaseDetailsPage';
import StockCardPage from './pages/StockCardPage';
import InventoryAdjustmentsPage from './pages/InventoryAdjustmentsPage';
import StockTransfersPage from './pages/StockTransfersPage';
import DryingPage from './pages/DryingPage';
import DryingForm from './pages/DryingForm';
import PalayHaulingPage from './pages/PalayHaulingPage';
import PalayHaulingForm from './pages/PalayHaulingForm';
import MillingPage from './pages/MillingPage';
import MillingForm from './pages/MillingForm';
import SalesPage from './pages/SalesPage';
import RiceAllowancePage from './pages/RiceAllowancePage';
import CustomersPage from './pages/CustomersPage';
import CustomerDetailPage from './pages/CustomerDetailPage';
import FarmsPage from './pages/FarmsPage';
import LivestockPage from './pages/LivestockPage';
import ReportsPage from './pages/ReportsPage';
import PartnersPage from './pages/PartnersPage';
import BranchesPage from './pages/BranchesPage';
import LocationsPage from './pages/LocationsPage';
import ItemsPage from './pages/ItemsPage';
import ItemCategoriesPage from './pages/ItemCategoriesPage';
import UnitsPage from './pages/UnitsPage';
import VehiclesPage from './pages/VehiclesPage';
import ChartOfAccountsPage from './pages/ChartOfAccountsPage';
import PaymentsPage from './pages/PaymentsPage';

import JournalEntriesPage from './pages/JournalEntriesPage';
import JournalEntryForm from './pages/JournalEntryForm';
import ApprovalsPage from './pages/ApprovalsPage';
import SubsidiaryLedgerPage from './pages/SubsidiaryLedgerPage';
import YearEndClosingPage from './pages/YearEndClosingPage';

import DashboardPage from './pages/DashboardPage';
import AdminPage from './pages/AdminPage'; // Admin panel with user management
import PwaInstallPrompt from './components/PwaInstallPrompt';


const ProtectedRoute = ({ children, allowedRoles }) => {
  const { user, isAuthenticated, loading } = useAuth();
  if (loading) return <div>Loading...</div>;
  if (!isAuthenticated) return <Navigate to="/login" />;

  if (allowedRoles && !allowedRoles.includes(user?.role)) {
    return <Navigate to="/" />; // Redirect unauthorized users to dashboard
  }

  return <AppLayout>{children}</AppLayout>;
};

const App = () => {
  return (
    <ConfigProvider theme={{ token: { primaryColor: '#1677ff', borderRadius: 6 } }}>
      <AuthProvider>
        <Router>
          <PwaInstallPrompt />
          <Routes>
            <Route path="/login" element={<LoginPage />} />
            <Route path="/" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF', 'VIEWER']}><DashboardPage /></ProtectedRoute>} />
            <Route path="/purchasing" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF', 'VIEWER']}><PurchasingPage /></ProtectedRoute>} />
            <Route path="/purchasing/new" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF']}><PurchaseForm /></ProtectedRoute>} />
            <Route path="/purchasing/:id" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF', 'VIEWER']}><PurchaseDetailsPage /></ProtectedRoute>} />
            <Route path="/inventory" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF', 'VIEWER']}><StockCardPage /></ProtectedRoute>} />
            <Route path="/inventory/adjustments" element={<ProtectedRoute allowedRoles={['ADMIN']}><InventoryAdjustmentsPage /></ProtectedRoute>} />
            <Route path="/hauling" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF', 'VIEWER']}><StockTransfersPage /></ProtectedRoute>} />
            <Route path="/hauling/new" element={<Navigate to="/hauling" replace />} />
            <Route path="/drying" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF', 'VIEWER']}><DryingPage /></ProtectedRoute>} />
            <Route path="/drying/new" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF']}><DryingForm /></ProtectedRoute>} />
            <Route path="/palay-hauling" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF', 'VIEWER']}><PalayHaulingPage /></ProtectedRoute>} />
            <Route path="/palay-hauling/new" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF']}><PalayHaulingForm /></ProtectedRoute>} />
            <Route path="/milling" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF', 'VIEWER']}><MillingPage /></ProtectedRoute>} />
            <Route path="/milling/new" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF']}><MillingForm /></ProtectedRoute>} />
            <Route path="/sales" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF', 'VIEWER']}><SalesPage /></ProtectedRoute>} />
            <Route path="/rice-allowances" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF', 'VIEWER']}><RiceAllowancePage /></ProtectedRoute>} />
            <Route path="/customers" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF', 'VIEWER']}><CustomersPage /></ProtectedRoute>} />
            <Route path="/customers/:id" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF', 'VIEWER']}><CustomerDetailPage /></ProtectedRoute>} />
            <Route path="/payments" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF', 'VIEWER']}><PaymentsPage /></ProtectedRoute>} />
            <Route path="/farms" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF', 'VIEWER']}><FarmsPage /></ProtectedRoute>} />
            <Route path="/livestock" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER', 'STAFF', 'VIEWER']}><LivestockPage /></ProtectedRoute>} />

            <Route path="/accounting/journal-entries" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER']}><JournalEntriesPage /></ProtectedRoute>} />
            <Route path="/accounting/journal-entries/new" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER']}><JournalEntryForm /></ProtectedRoute>} />
            <Route path="/accounting/journal-entries/:id/edit" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER']}><JournalEntryForm /></ProtectedRoute>} />
            <Route path="/accounting/reports" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER']}><ReportsPage /></ProtectedRoute>} />
            <Route path="/accounting/coa" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER']}><ChartOfAccountsPage /></ProtectedRoute>} />
            <Route path="/accounting/subsidiary-ledgers" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER']}><SubsidiaryLedgerPage /></ProtectedRoute>} />
            <Route path="/accounting/year-end-closing" element={<ProtectedRoute allowedRoles={['ADMIN']}><YearEndClosingPage /></ProtectedRoute>} />
            <Route path="/approvals" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER']}><ApprovalsPage /></ProtectedRoute>} />

            <Route path="/partners" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER']}><PartnersPage /></ProtectedRoute>} />
            <Route path="/master/branches" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER']}><BranchesPage /></ProtectedRoute>} />
            <Route path="/master/locations" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER']}><LocationsPage /></ProtectedRoute>} />
            <Route path="/master/items" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER']}><ItemsPage /></ProtectedRoute>} />
            <Route path="/master/categories" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER']}><ItemCategoriesPage /></ProtectedRoute>} />
            <Route path="/master/units" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER']}><UnitsPage /></ProtectedRoute>} />
            <Route path="/master/vehicles" element={<ProtectedRoute allowedRoles={['ADMIN', 'MANAGER']}><VehiclesPage /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute allowedRoles={['ADMIN']}><AdminPage /></ProtectedRoute>} />
          </Routes>
        </Router>
      </AuthProvider>
    </ConfigProvider>
  );
};

export default App;
